// Which version of each *interventiebibliotheek* we serve, and how to address it.
//
// WHY THIS IS A SEPARATE FILE FROM standaardVersions.js
//
// The meetstandaard api serves two document kinds from the same URL shape:
//
//   meta.kind absent          → effecten → stellingen   (a questionnaire catalogue)
//   meta.kind 'interventie…'  → interventies → kengetallen (a calculation catalogue)
//
// PINNED_STANDAARD_VERSIONS is enumerated by three consumers that all assume
// the first kind: EffectDatabase (which pipes every entry through
// adaptMeetstandaardDocument inside a Promise.all), useMsiMonetaryScores, and
// the nightly fetchMsiScores. An interventiebibliotheek has no `effecten`, so
// adding one to that map takes the whole effect picker down — and because the
// Promise.all rejects, it reads to the user as "that effect doesn't exist"
// rather than as an error. Hence a second map, deliberately not merged.
//
// PINNING IS DELIBERATE, same as the effect standaarden: a published version is
// immutable, and the kengetal behind an already-reported CO2 saving must not
// shift because upstream published a new one. Bumping is a migration decision.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import { MEETSTANDAARD_PATH } from './standaardVersions'

/**
 * Standaard slug → pinned version id.
 *
 * Only interventiebibliotheken belong here. Effect standaarden live in
 * PINNED_STANDAARD_VERSIONS and the two lists must stay disjoint — a pinning
 * test asserts exactly that.
 *
 * @type {Object<string, string>}
 */
const PINNED_INTERVENTIE_VERSIONS = {
    'milieu-circulariteit': '0.11',
}

/**
 * @returns {Array<string>} the interventiebibliotheken we serve
 */
const pinnedInterventieStandaarden = () => Object.keys(PINNED_INTERVENTIE_VERSIONS)

/**
 * URL of one immutable published version.
 *
 * Throws rather than falling back to an unpinned route: quietly fetching
 * "newest" is the failure mode this module exists to prevent.
 *
 * @param {string} standaard - e.g. 'milieu-circulariteit'
 * @param {string} version - An exact version id such as '0.9'
 * @returns {string}
 */
const interventieDocumentUrl = (standaard, version) => {
    if (!standaard) throw new Error('interventieDocumentUrl: standaard is required')
    if (!version || version === 'latest') {
        throw new Error(`interventieDocumentUrl: an exact version is required for '${standaard}', got '${version}'`)
    }
    return `${MEETSTANDAARD_PATH}/${standaard}/${version}`
}

/**
 * URL listing the versions that exist upstream — for checking whether our pin
 * has fallen behind, never for fetching content.
 *
 * @param {string} standaard
 * @returns {string}
 */
const interventieVersionsUrl = (standaard) => {
    if (!standaard) throw new Error('interventieVersionsUrl: standaard is required')
    return `${MEETSTANDAARD_PATH}/${standaard}/versions`
}

export {
    PINNED_INTERVENTIE_VERSIONS,
    pinnedInterventieStandaarden,
    interventieDocumentUrl,
    interventieVersionsUrl,
}
